import {uniq} from "lodash";
import {ParamSchema} from "../../apis/requests/segments/types";
import {ReachTemplateContentChannel} from "../../apis/requests/templates/types";
import {
  TriggerCondition,
  TriggerOperator,
  TriggerRule,
  TriggerRuleValue,
  TriggerTemplateSummary,
} from "../../apis/requests/triggers/types";
import {reachUrls} from "../../layout/menu-items";
import {findParam, makeDefaultCondition, normalizeSubmitValue} from "../segments/segment-form/helpers";
import {CHANNEL_DISPLAY} from "../templates/templateDisplay";
import {NEEDS_LANDING_PAGE_SOURCE} from "../templates/templateFormSchema";
import {NO_VALUE_OPERATORS, OPERATOR_LABELS, TIMING_DISPLAY} from "./triggerDisplay";

export type TemplateChannels = Map<number, ReachTemplateContentChannel>;

export const triggerUrl = (id: number): string => `${reachUrls.triggers}/${id}`;

export const templateUrl = (id: number): string => `${reachUrls.templates}/${id}`;

export const makeRule = (parameters: ParamSchema[]): TriggerRule => {
  const {parameter, operator, value} = makeDefaultCondition(parameters);
  return {field: parameter, operator: operator as TriggerOperator, value: value as TriggerRuleValue};
};

export const normalizeRuleValue = (parameters: ParamSchema[], rule: TriggerRule): TriggerRuleValue =>
  NO_VALUE_OPERATORS.has(rule.operator)
    ? null
    : (normalizeSubmitValue(findParam(parameters, rule.field), rule.operator, rule.value) as TriggerRuleValue);

export const ruleError = (rule: TriggerRule, value: TriggerRuleValue): string | null => {
  if (!rule.field) return "Pick a field for every rule";
  if (NO_VALUE_OPERATORS.has(rule.operator)) return null;
  if (Array.isArray(value) ? value.length === 0 : value === null || value === "") {
    return `Enter a value for ${rule.field}`;
  }
  return null;
};

export const ruleSummary = (rule: TriggerRule): string => {
  const operator = OPERATOR_LABELS[rule.operator] ?? rule.operator;
  if (NO_VALUE_OPERATORS.has(rule.operator)) return `${rule.field} ${operator}`;
  const value = Array.isArray(rule.value) ? rule.value.join(", ") : String(rule.value ?? "");
  return `${rule.field} ${operator} ${value}`;
};

const channelLabel = (channels: TemplateChannels, templateId: number | null): string | null => {
  if (!templateId) return null;
  const channel = channels.get(templateId);
  return channel ? CHANNEL_DISPLAY[channel].label : "Unknown template";
};

export const conditionChannels = (condition: TriggerCondition, channels: TemplateChannels): string[] => {
  if (condition.outcome === "do_not_send") return [];
  return [condition.email_template_id, condition.text_template_id]
    .map((id) => channelLabel(channels, id))
    .filter((label): label is string => !!label);
};

export const triggerChannelsLabel = (conditions: TriggerCondition[], channels: TemplateChannels): string =>
  uniq(conditions.flatMap((condition) => conditionChannels(condition, channels))).join(", ");

export const triggerTimingLabel = (conditions: TriggerCondition[]): string => {
  const timings = uniq(conditions.filter((c) => c.outcome === "send").map((c) => c.timing));
  if (timings.length === 0) return "";
  if (timings.length > 1) return "Mixed";
  return TIMING_DISPLAY[timings[0]].shortLabel;
};

export const textChannelLabel = (channel: ReachTemplateContentChannel) =>
  channel.startsWith("mms") ? "MMS" : channel.startsWith("sms") ? "SMS" : CHANNEL_DISPLAY[channel].label;

export const accountSummary = (account: {first_name: string; last_name: string; account_public_id: string}) =>
  [`${account.first_name} ${account.last_name}`.trim(), account.account_public_id].filter(Boolean).join(" · ");

export const examplePayload = (parameters: ParamSchema[]): Record<string, unknown> =>
  Object.fromEntries(
    parameters.map((parameter) => [
      parameter.parameter,
      parameter.kind === "boolean" ? false : parameter.kind === "numeric" ? 0 : parameter.options?.[0]?.value ?? "",
    ])
  );

export type PreviewTab = "email" | "text" | "landing";

export const previewTabs = (
  emailTemplate: TriggerTemplateSummary | null,
  textTemplate: TriggerTemplateSummary | null
): {tab: PreviewTab; label: string; template: TriggerTemplateSummary}[] => [
  ...(emailTemplate ? [{tab: "email" as const, label: "Email", template: emailTemplate}] : []),
  ...(textTemplate
    ? [{tab: "text" as const, label: textChannelLabel(textTemplate.content_channel), template: textTemplate}]
    : []),
  ...(textTemplate && NEEDS_LANDING_PAGE_SOURCE.has(textTemplate.content_channel)
    ? [{tab: "landing" as const, label: "Landing page", template: textTemplate}]
    : []),
];
